import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Minus, Plus, Trash2, ShoppingBag, Send, Table2, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MobileLayout } from "@/components/layouts/MobileLayout";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

import { formatPrice } from "@/utils/currency";
import API from "@/utils/api";
const TABLE_QR_KEY = "app_qr_table";

export default function CartPage() {
  const navigate = useNavigate();
  const { items, updateQuantity, removeItem, getTotal, clearCart } = useCart();
  const { user } = useAuth();

  // Check if ordering from QR table
  const tableNumber = localStorage.getItem(TABLE_QR_KEY);
  const isTableMode = !!tableNumber;

  const [orderNotes, setOrderNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [orderTotal, setOrderTotal] = useState(0);
  const [orderId, setOrderId] = useState("");

  const handleCheckout = () => {
    if (!user) {
      // Save the destination path for after login
      localStorage.setItem("redirect_after_login", "/order-type");
      navigate("/login", { state: { from: "/order-type" } });
      return;
    }
    navigate("/order-type");
  };

  // Submit order directly from table (QR mode)
  const handleTableOrder = async () => {
    if (items.length === 0) {
      toast.error("El carrito está vacío");
      return;
    }

    setIsSubmitting(true);
    try {
      const orderData = {
        items: items.map(item => ({
          item_id: item.item_id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
          extras: item.extras || [],
          notes: item.notes || ""
        })),
        table_number: tableNumber,
        notes: orderNotes
      };

      // Use table endpoint (no auth required)
      const branchId = localStorage.getItem("app_qr_branch");
      const config = {};
      if (branchId) {
        config.headers = { 'X-Branch-Id': branchId };
      }
      const response = await axios.post(`${API}/orders/table`, orderData, config);

      // Show success dialog
      setOrderTotal(getTotal());
      setOrderId(response.data.order_id);
      setShowSuccess(true);

      // Clear cart
      clearCart();

    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Error al enviar el pedido. Inténtalo de nuevo.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle closing success dialog
  const handleSuccessClose = () => {
    setShowSuccess(false);
    // DO NOT clear TABLE_QR_KEY here, we want to stay "seated"

    // Redirect back to menu preserving the query string (mesa, sucursal)
    const qs = window.location.search;
    navigate(`/menu${qs}`);
  };

  if (items.length === 0 && !showSuccess) {
    return (
      <MobileLayout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
          <div className="w-20 h-20 bg-gray-800 rounded-full flex items-center justify-center mb-4">
            <ShoppingBag className="w-10 h-10 text-gray-500" />
          </div>
          <h2 className="font-heading text-xl font-bold mb-2 text-white">
            Tu carrito está vacío
          </h2>
          <p className="text-gray-400 text-center mb-6">
            Añade algunos productos deliciosos para empezar
          </p>
          <Button
            className="rounded-full bg-primary hover:opacity-90 text-black font-bold"
            style={{ backgroundColor: 'var(--primary)' }}
            onClick={() => navigate("/menu")}
            data-testid="cart-browse-menu"
          >
            Ver menú
          </Button>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      {/* Table Banner */}
      {isTableMode && (
        <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-3 flex items-center justify-center gap-2 shadow-lg">
          <Table2 className="w-5 h-5" />
          <span className="font-bold">Pedido para Mesa {tableNumber}</span>
        </div>
      )}

      <div className="pb-40">
        {/* Header */}
        <div className="sticky top-0 bg-black z-30 px-4 py-4 border-b border-gray-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="text-white hover:bg-gray-800"
                data-testid="cart-back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="font-heading text-xl font-bold text-white">Tu carrito</h1>
                {isTableMode && (
                  <p className="text-green-400 text-sm flex items-center gap-1">
                    <Table2 className="w-3 h-3" />
                    Mesa {tableNumber}
                  </p>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
              onClick={clearCart}
              data-testid="cart-clear-btn"
            >
              Vaciar
            </Button>
          </div>
        </div>

        {/* Cart Items */}
        <div className="px-4 py-4 space-y-4">
          {items.map((item, index) => {
            const itemPrice = parseFloat(item.price) || 0;
            const extrasTotal = item.extras?.reduce((sum, e) => sum + (parseFloat(e.price) || 0), 0) || 0;
            const itemTotal = (itemPrice + extrasTotal) * item.quantity;

            return (
              <div
                key={index}
                data-testid={`cart-item-${index}`}
                className="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden"
              >
                <div className="flex p-4">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 rounded-xl object-cover bg-gray-800"
                    onError={(e) => e.target.src = "https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=200"}
                  />
                  <div className="flex-1 ml-4">
                    <h3 className="font-heading font-bold text-white">{item.name}</h3>
                    <p className="text-sm text-primary" style={{ color: 'var(--primary)' }}>
                      {formatPrice(itemPrice)}
                    </p>

                    {item.extras?.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {item.extras.map((extra) => (
                          <span
                            key={extra.name}
                            className="text-xs bg-gray-800 text-gray-300 px-2 py-0.5 rounded-full"
                          >
                            +{extra.name}
                          </span>
                        ))}
                      </div>
                    )}

                    {item.notes && (
                      <p className="text-xs text-gray-400 mt-1 italic">
                        "{item.notes}"
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between px-4 py-3 bg-gray-800/50 border-t border-gray-800">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 rounded-full border-gray-700 text-white hover:bg-gray-700"
                      onClick={() => updateQuantity(index, item.quantity - 1)}
                      data-testid={`cart-item-${index}-minus`}
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                    <span className="w-6 text-center font-bold text-white">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 rounded-full border-gray-700 text-white hover:bg-gray-700"
                      onClick={() => updateQuantity(index, item.quantity + 1)}
                      data-testid={`cart-item-${index}-plus`}
                    >
                      <Plus className="w-3 h-3" />
                    </Button>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="font-bold text-white">{formatPrice(itemTotal)}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-900/20"
                      onClick={() => removeItem(index)}
                      data-testid={`cart-item-${index}-remove`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Order Notes - Only in table mode */}
          {isTableMode && (
            <div className="bg-gray-900 rounded-2xl border border-gray-800 p-4">
              <label className="text-sm text-gray-400 mb-2 block">Notas del pedido (opcional)</label>
              <Textarea
                placeholder="Ej: Sin cebolla, extra picante..."
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                className="bg-gray-800 border-gray-700 text-white resize-none"
                rows={2}
              />
            </div>
          )}
        </div>
      </div>

      {/* Checkout Footer */}
      <div className="fixed bottom-20 left-0 right-0 bg-gray-900 border-t border-gray-800 p-4 z-40">
        <div className="flex items-center justify-between mb-3">
          <span className="text-gray-400">Total</span>
          <span className="font-bold text-2xl text-primary" style={{ color: 'var(--primary)' }}>{formatPrice(getTotal())}</span>
        </div>

        {isTableMode ? (
          // Table/QR mode - Send order to kitchen
          <div className="space-y-2">
            <div className="bg-green-900/30 border border-green-600 rounded-lg px-3 py-2 text-center">
              <p className="text-xs text-green-400">Enviarás este pedido a:</p>
              <div className="flex items-center justify-center gap-2 mt-1">
                <Table2 className="w-4 h-4 text-green-400" />
                <p className="font-bold text-green-300">Mesa {tableNumber}</p>
              </div>
            </div>
            <Button
              className="w-full h-12 rounded-full font-bold text-base bg-green-500 hover:bg-green-600 text-black"
              onClick={handleTableOrder}
              disabled={isSubmitting || items.length === 0}
              data-testid="cart-send-order-btn"
            >
              {isSubmitting ? (
                "Enviando..."
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Enviar Pedido a Cocina
                </>
              )}
            </Button>
          </div>
        ) : (
          // Normal mode - Go to checkout
          <Button
            className="w-full h-12 rounded-full font-bold text-base bg-primary hover:opacity-90 text-black"
            style={{ backgroundColor: 'var(--primary)' }}
            onClick={handleCheckout}
            data-testid="cart-checkout-btn"
          >
            Continuar al pago
          </Button>
        )}
      </div>

      {/* Success Dialog */}
      <Dialog open={showSuccess} onOpenChange={handleSuccessClose}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-sm mx-4">
          <DialogHeader>
            <DialogTitle className="text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <span className="text-2xl font-heading text-green-400">¡Pedido Enviado!</span>
            </DialogTitle>
          </DialogHeader>

          <div className="text-center py-4 space-y-4">
            <div className="bg-gray-800 rounded-xl p-4">
              <p className="text-gray-400 text-sm">Número de pedido</p>
              <p className="text-xl font-bold text-white">#{orderId.slice(-6).toUpperCase()}</p>
            </div>

            <div className="bg-primary/20 border border-primary rounded-xl p-4" style={{ backgroundColor: 'rgba(var(--primary-rgb), 0.2)', borderColor: 'var(--primary)' }}>
              <p className="text-primary text-sm mb-1" style={{ color: 'var(--primary)' }}>Total a pagar en caja</p>
              <p className="text-3xl font-bold text-primary" style={{ color: 'var(--primary)' }}>{formatPrice(orderTotal)}</p>
            </div>

            <p className="text-gray-400 text-sm">
              Tu pedido está siendo preparado.<br />
              Paga en caja cuando estés listo.
            </p>
          </div>

          <Button
            onClick={handleSuccessClose}
            className="w-full bg-green-500 hover:bg-green-600 text-black font-bold"
          >
            Entendido
          </Button>
        </DialogContent>
      </Dialog>
    </MobileLayout>
  );
}
